import React, { useEffect, useState } from 'react';
import { Cpu, Layers3, Network, RefreshCw, Sparkles, Waypoints } from 'lucide-react';
import { toast } from '../ui/sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MetricCard = ({ label, value, helper, testId }) => (
  <div className="glass-card p-5" data-testid={testId}>
    <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">{label}</p>
    <p className="mt-3 text-3xl font-bold text-text-heading">{value}</p>
    <p className="mt-2 text-xs text-text-muted">{helper}</p>
  </div>
);

export const AgentArchitectureHub = ({ authFetch }) => {
  const [hub, setHub] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadHub = async () => {
    try {
      setLoading(true);
      const res = await authFetch(`${API}/admin/agent-architecture`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'No se pudo cargar arquitectura IA');
      setHub(data);
    } catch (error) {
      toast.error(error.message || 'No se pudo cargar arquitectura IA');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHub(); }, []);

  if (!hub) return <div className="glass-card p-8 text-center text-text-muted" data-testid="agent-architecture-loading">Cargando arquitectura IA...</div>;

  return (
    <div className="space-y-6" data-testid="agent-architecture-hub">
      <div className="glass-card tracing-panel overflow-hidden" data-testid="agent-architecture-hero-card">
        <div className="grid gap-6 p-6 xl:grid-cols-[1.08fr_0.92fr] xl:p-8">
          <div>
            <p className="editorial-kicker mb-3">Arquitectura IA</p>
            <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Diagnóstico aplicado · mapa maestro Aynara + MCP + agentes simples</p>
            <h3 className="mt-3 text-3xl font-semibold text-text-heading">Neuralia deja de parecer una suma de módulos y empieza a verse como un sistema operativo conversacional completo.</h3>
            <p className="mt-4 max-w-3xl text-sm text-text-body">Aynara es ahora el centro conceptual: una capa principal que dirige agentes, contexto, herramientas externas y ciclos LLOPS.</p>
            <div className="mt-5 flex flex-wrap gap-2" data-testid="agent-architecture-diagnosis-chips">
              <span className="insight-chip">Aynara core</span>
              <span className="insight-chip">MCP-style hub</span>
              <span className="insight-chip">Agentes simples</span>
              <span className="insight-chip">LLOPS nativo</span>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <MetricCard label="Agentes visión" value={hub.metrics.vision_total_agents} helper="objetivo total" testId="agent-architecture-metric-vision" />
            <MetricCard label="Agentes lanzamiento" value={hub.metrics.launch_layer_agents} helper="capa de salida real" testId="agent-architecture-metric-launch" />
            <MetricCard label="Departamentos" value={hub.metrics.departments} helper="capas organizadas" testId="agent-architecture-metric-departments" />
            <MetricCard label="Agentes live" value={hub.metrics.live_agent_types} helper="tipos en uso" testId="agent-architecture-metric-live" />
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={loadHub} className="btn-secondary" disabled={loading} data-testid="agent-architecture-refresh-button">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refrescar
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
        <div className="glass-card p-6" data-testid="agent-architecture-aynara-card">
          <div className="flex items-center gap-2 text-text-heading"><Sparkles className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Identidad central de Aynara</h4></div>
          <p className="mt-4 text-2xl font-semibold text-text-heading">{hub.aynara_identity.name}</p>
          <p className="mt-2 text-sm text-text-body">{hub.aynara_identity.role}</p>
          <p className="mt-4 text-sm text-text-body">{hub.aynara_identity.mission}</p>
          <div className="mt-4 flex flex-wrap gap-2" data-testid="agent-architecture-aynara-tone-chips">
            {hub.aynara_identity.tone.map((item, index) => <span key={`${item}-${index}`} className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{item}</span>)}
          </div>
        </div>

        <div className="glass-card p-6" data-testid="agent-architecture-mcp-model-card">
          <div className="flex items-center gap-2 text-text-heading"><Layers3 className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Modelo MCP-style</h4></div>
          <p className="mt-4 text-sm text-text-body">{hub.mcp_operating_model.promise}</p>
          <div className="mt-5 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {hub.mcp_operating_model.layers.map((item, index) => <div key={`${item}-${index}`} className="rounded-[20px] border border-border-subtle bg-card/40 p-4" data-testid={`agent-architecture-mcp-layer-${index}`}><p className="text-sm font-semibold text-text-heading">{item}</p></div>)}
          </div>
        </div>
      </div>

      <div className="glass-card p-6" data-testid="agent-architecture-simple-agents-card">
        <div className="flex items-center gap-2 text-text-heading"><Waypoints className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Familia de agentes simples Neuralia</h4></div>
        <div className="mt-5 grid gap-4 xl:grid-cols-3">
          {hub.simple_agent_family.map((item, index) => (
            <div key={`${item}-${index}`} className="rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`agent-architecture-simple-agent-${index}`}>
              <p className="text-sm font-semibold text-text-heading">{item}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid gap-6 xl:grid-cols-[1.08fr_0.92fr]">
        <div className="glass-card p-6" data-testid="agent-architecture-phases-card">
          <div className="flex items-center gap-2 text-text-heading"><Network className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">Fases y departamentos del sistema</h4></div>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {hub.phases.map((phase, index) => (
              <div key={phase.id || index} className="rounded-[24px] border border-border-subtle bg-background/55 p-4" data-testid={`agent-architecture-phase-${index}`}>
                <p className="text-[10px] uppercase tracking-[0.18em] text-text-muted">Fase {index + 1}</p>
                <p className="mt-2 text-base font-semibold text-text-heading">{phase.name}</p>
                <p className="mt-2 text-sm text-text-body">{phase.description}</p>
              </div>
            ))}
          </div>
          <div className="mt-5 space-y-3">
            {hub.departments.map((department, index) => (
              <div key={department.department} className="rounded-[24px] border border-border-subtle bg-card/40 p-4" data-testid={`agent-architecture-department-${index}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-text-heading">{department.department}</p>
                    <p className="mt-1 text-xs text-text-muted">{department.owner}</p>
                  </div>
                  <span className="rounded-full border border-primary/20 bg-primary/10 px-2 py-1 text-[10px] uppercase tracking-[0.16em] text-primary">{department.launch_agents} live</span>
                </div>
                <p className="mt-3 text-sm text-text-body">{department.why_now}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="space-y-6">
          <div className="glass-card p-6" data-testid="agent-architecture-llops-card">
            <div className="flex items-center gap-2 text-text-heading"><Cpu className="w-5 h-5 text-primary" /><h4 className="text-lg font-semibold">LLOPS integrado en todos los agentes</h4></div>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {hub.llops_cycle.map((item, index) => <div key={`${item}-${index}`} className="rounded-[20px] border border-border-subtle bg-background/55 p-4" data-testid={`agent-architecture-llops-${index}`}><p className="text-sm font-semibold text-text-heading">{item}</p></div>)}
            </div>
          </div>

          <div className="glass-card p-6" data-testid="agent-architecture-principles-card">
            <h4 className="text-lg font-semibold text-text-heading">Principios rectores</h4>
            <div className="mt-4 space-y-3">
              {hub.principles.map((item, index) => <div key={`${item}-${index}`} className="rounded-[20px] border border-border-subtle bg-card/40 p-4 text-sm text-text-body" data-testid={`agent-architecture-principle-${index}`}>{item}</div>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentArchitectureHub;